import React, { useState, useRef, useEffect } from "react";
import Select from "react-select";
import { XIcon } from "@heroicons/react/outline";
import { toast } from "react-toastify";
import { AxiosResponse } from "axios";
import Loader from "../../shared/loader/loader";
import {
  getCanvasAccountSettingsByDistrictId,
  addCanvasAccountSettings,
  updateCanvasAccountSettings,
  getCanvasAccountSchoolsByDistrictId,
  updateCanvasAccountSchools,
} from "../../../api/miniCrm.service";
import {
  ICanvasSchool,
  ICanvasSchoolRequest,
} from "../../../model/interface/ICanvasSchool";
import { IResult } from "../../../model/interface/result";
import { ICanvasAccountSettings } from "../../../model/interface/ICanvasAccountSettings";

interface ICanvasAccountSettingsProps {
  districtId: number;
  onClose: () => void;
}

const emptyGroup = (
  groupNumber: number,
  districtId: number
): ICanvasAccountSettings => ({
  clientId: "",
  clientSecret: "",
  endPointURL: "",
  districtId,
  groupNumber,
  canvasScoringTypeId: 1,
});

export const CanvasAccountSettings: React.FC<ICanvasAccountSettingsProps> = ({
  districtId,
  onClose,
}) => {
  type tab = "CANVAS_ACCOUNT_SETTINGS";
  const [loading, setLoading] = useState<boolean>(false);
  const [activeTab] = useState<tab>("CANVAS_ACCOUNT_SETTINGS");
  const [canvasAccountSettings, setCanvasAccountSettings] = useState<
    ICanvasAccountSettings[]
  >([emptyGroup(1, districtId)]);
  const [canvasSchools, setCanvasSchools] = useState<ICanvasSchool[]>([]);
  const originalCanvasSchools = useRef<ICanvasSchool[]>([]);
  const originalGroupNumbers = useRef<Set<number>>(new Set());
  const [canvasDetailsIsValid, setCanvasDetailIsValid] =
    useState<boolean>(true);
  const [canvasDetailsIsEdit, setCanvasDetailsIsEdit] =
    useState<boolean>(false);

  const activeTabClass =
    "text-gray-900 topBorder bg-white border border-b-0 group relative min-w-0 flex-1 mb-0 overflow-hidden py-4 px-4 text-sm font-medium text-center hover:bg-gray-50 dark:hover:bg-gray-700 focus:z-10";
  const inActiveTabClass =
    "text-gray-500 hover:text-gray-700 group relative min-w-0 flex-1 overflow-hidden mb-0 py-4 px-4 text-sm font-medium text-center hover:bg-gray-50 focus:z-10";

  const canvasScoringTypeOptionsForSchools: {
    label: string;
    value: number | null;
  }[] = [
    { label: "Inherited", value: null },
    { label: "Points", value: 1 },
    { label: "Percentage", value: 2 },
    { label: "Points Out Of 100", value: 3 },
  ];

  const canvasScoringTypeOptionsForDistricts: {
    label: string;
    value: number | null;
  }[] = [
    { label: "Points", value: 1 },
    { label: "Percentage", value: 2 },
    { label: "Points Out Of 100", value: 3 },
  ];

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    getCanvasAccountSettingsByDistrictId(districtId)
      .then((resp: AxiosResponse<IResult<ICanvasAccountSettings[]>>) => {
        if (resp.data?.data?.length > 0) {
          setCanvasAccountSettings(resp.data.data);
          originalGroupNumbers.current = new Set(
            resp.data.data.map((s: ICanvasAccountSettings) => s.groupNumber)
          );
          setCanvasDetailsIsEdit(true);
          getCanvasAccountSchoolsByDistrictId(districtId)
            .then((schools: AxiosResponse<IResult<ICanvasSchool[]>>) => {
              setCanvasSchools(schools.data.data);
              originalCanvasSchools.current = schools.data.data;
            })
            .finally(() => {
              setLoading(false);
            });
        } else {
          setCanvasAccountSettings([emptyGroup(1, districtId)]);
          originalGroupNumbers.current = new Set();
          setCanvasDetailsIsEdit(false);
          setCanvasSchools([]);
          setLoading(false);
        }
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const handleCanvasAccountSettingChange = (
    index: number,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target;
    setCanvasAccountSettings((prev) =>
      prev.map((s, i) => (i === index ? { ...s, [name]: value } : s))
    );
  };

  const handleDistrictCanvasScoringTypeChange = (
    index: number,
    value: number | null
  ) => {
    setCanvasAccountSettings((prev) =>
      prev.map((s, i) =>
        i === index
          ? { ...s, canvasScoringTypeId: value ?? s.canvasScoringTypeId }
          : s
      )
    );
  };

  const addGroup = () => {
    const nextGroupNumber =
      Math.max(...canvasAccountSettings.map((s) => s.groupNumber)) + 1;
    setCanvasAccountSettings((prev) => [
      ...prev,
      emptyGroup(nextGroupNumber, districtId),
    ]);
  };

  const removeGroup = (index: number) => {
    setCanvasAccountSettings((prev) => prev.filter((_, i) => i !== index));
  };

  const validate = () => {
    const allValid = canvasAccountSettings.every(
      (s) => s.clientId !== "" && s.clientSecret !== "" && s.endPointURL !== ""
    );
    setCanvasDetailIsValid(allValid);
    return allValid;
  };

  const handleSaveCanvasAccountSettings = () => {
    if (!validate()) return;

    const savePromises = canvasAccountSettings.map((settings) => {
      const payload = { ...settings, districtId, schoolId: 0 };
      return originalGroupNumbers.current.has(settings.groupNumber)
        ? updateCanvasAccountSettings(payload)
        : addCanvasAccountSettings(payload);
    });

    setLoading(true);
    Promise.all(savePromises)
      .then((results) => {
        const allSucceeded = results.every((r) => r.data.data > 0);
        if (allSucceeded) {
          toast.success(
            canvasDetailsIsEdit
              ? "Settings updated successfully"
              : "Settings added successfully"
          );
          loadSettings();
        } else {
          toast.error("Some settings failed to save");
        }
      })
      .catch(() => {
        toast.error("Failed to save settings");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleSaveCanvasAccountSchools = () => {
    const changed: ICanvasSchoolRequest[] = canvasSchools
      .filter((school) => {
        const original = originalCanvasSchools.current.find(
          (o) => o.schoolId === school.schoolId
        );
        return (
          original?.canvasScoringTypeId !== school.canvasScoringTypeId ||
          original?.groupNumber !== school.groupNumber
        );
      })
      .map(({ schoolId, canvasScoringTypeId, groupNumber }) => ({
        schoolId,
        canvasScoringTypeId,
        groupNumber,
      }));

    if (changed.length === 0) {
      toast.info("No changes to save");
      return;
    }

    setLoading(true);
    updateCanvasAccountSchools(changed)
      .then((resp) => {
        if (resp.data.isSuccess) {
          originalCanvasSchools.current = [...canvasSchools];
          toast.success("Schools updated successfully");
        } else {
          toast.error("Failed to update schools");
        }
      })
      .catch(() => {
        toast.error("Failed to update schools");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleSchoolCanvasScoringTypeChange = (
    schoolId: number,
    value: number | null
  ) => {
    setCanvasSchools((prev) =>
      prev.map((s) =>
        s.schoolId === schoolId ? { ...s, canvasScoringTypeId: value } : s
      )
    );
  };

  const handleSchoolCanvasGroupNumberChange = (
    schoolId: number,
    groupNumber: number
  ) => {
    setCanvasSchools((prev) =>
      prev.map((s) => (s.schoolId === schoolId ? { ...s, groupNumber } : s))
    );
  };

  const groupOptions = canvasAccountSettings.map((s) => ({
    label: `Group ${s.groupNumber}`,
    value: s.groupNumber,
  }));

  const inputClass =
    "mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-color focus:border-primary-color dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white sm:text-sm";

  return (
    <>
      {loading && <Loader />}
      <div className="w-full justify-center items-center flex my-auto h-full fixed inset-0 z-50 outline-none focus:outline-none">
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          aria-hidden="true"
        ></div>

        <div className="relative h-full w-5/6 my-6 mx-auto">
          <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full max-h-full overflow-hidden bg-white outline-none focus:outline-none">
            <div className="relative p-6 flex-auto overflow-y-auto">
              <div className="flex justify-end">
                <button onClick={onClose} className="text-xl">
                  <XIcon className="w-6" />
                </button>
              </div>
              <div className="pt-1">
                <div className="flex items-center justify-between bg-gray-100 w-full">
                  <div className="flex">
                    <h2
                      className={
                        activeTab === "CANVAS_ACCOUNT_SETTINGS"
                          ? activeTabClass
                          : inActiveTabClass
                      }
                    >
                      Canvas Account Settings
                    </h2>
                  </div>
                </div>
              </div>

              <div id="body">
                {activeTab === "CANVAS_ACCOUNT_SETTINGS" && (
                  <>
                    <form id="canvas-settings-form">
                      <div>
                        <h1 className="mt-2 text-lg font-light text-gray-900">
                          Canvas Settings
                        </h1>
                      </div>

                      {canvasAccountSettings.map((settings, index) => (
                        <div
                          key={settings.groupNumber}
                          className="mt-4 border border-gray-200 rounded-lg p-4"
                        >
                          <div className="flex items-center justify-between mb-3">
                            <h2 className="text-sm font-medium text-gray-700">
                              Group {settings.groupNumber}
                            </h2>
                            {!originalGroupNumbers.current.has(
                              settings.groupNumber
                            ) && (
                              <button
                                type="button"
                                onClick={() => removeGroup(index)}
                                className="text-red-500 hover:text-red-700 text-sm"
                              >
                                Remove
                              </button>
                            )}
                          </div>

                          <div className="grid grid-cols-3 gap-4">
                            <div>
                              <span className="whitespace-nowrap">
                                Client Id
                              </span>
                              <input
                                type="text"
                                autoComplete="off"
                                value={settings.clientId}
                                className={inputClass}
                                name="clientId"
                                onChange={(e) =>
                                  handleCanvasAccountSettingChange(index, e)
                                }
                              />
                              {!canvasDetailsIsValid &&
                                settings.clientId === "" && (
                                  <span className="text-red-500 max-w-70% p-0.5 mt-2 pr-3 text-sm">
                                    Client Id is required
                                  </span>
                                )}
                            </div>

                            <div>
                              <span className="whitespace-nowrap">
                                Client Secret Id
                              </span>
                              <input
                                value={settings.clientSecret}
                                type="text"
                                autoComplete="off"
                                className={inputClass}
                                name="clientSecret"
                                onChange={(e) =>
                                  handleCanvasAccountSettingChange(index, e)
                                }
                              />
                              {!canvasDetailsIsValid &&
                                settings.clientSecret === "" && (
                                  <span className="text-red-500 max-w-70% p-0.5 mt-2 pr-3 text-sm">
                                    Client Secret Id is required
                                  </span>
                                )}
                            </div>

                            <div>
                              <span className="whitespace-nowrap">
                                EndPoint URL
                              </span>
                              <input
                                value={settings.endPointURL}
                                type="text"
                                autoComplete="off"
                                className={inputClass}
                                name="endPointURL"
                                onChange={(e) =>
                                  handleCanvasAccountSettingChange(index, e)
                                }
                              />
                              {!canvasDetailsIsValid &&
                                settings.endPointURL === "" && (
                                  <span className="text-red-500 max-w-70% p-0.5 mt-2 pr-3 text-sm">
                                    EndPoint URL is required
                                  </span>
                                )}
                            </div>

                            <div>
                              <span className="whitespace-nowrap">
                                Default Canvas Scoring Type
                              </span>
                              <Select
                                className="text-sm"
                                value={
                                  canvasScoringTypeOptionsForDistricts.find(
                                    (o) =>
                                      o.value === settings.canvasScoringTypeId
                                  ) ?? canvasScoringTypeOptionsForDistricts[0]
                                }
                                options={canvasScoringTypeOptionsForDistricts}
                                onChange={(option) =>
                                  handleDistrictCanvasScoringTypeChange(
                                    index,
                                    option?.value ?? null
                                  )
                                }
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={{
                                  menuPortal: (base) => ({
                                    ...base,
                                    zIndex: 9999,
                                  }),
                                }}
                                theme={(theme) => ({
                                  ...theme,
                                  colors: {
                                    ...theme.colors,
                                    primary25: "#B3B3B3",
                                    primary: "#041E42",
                                  },
                                })}
                              />
                            </div>
                          </div>
                        </div>
                      ))}

                      <div className="flex justify-between items-center mt-4">
                        <button
                          type="button"
                          onClick={addGroup}
                          className="text-sm text-primary-color border border-primary-color hover:bg-gray-50 py-1.5 px-4 rounded-lg"
                        >
                          + Add Group
                        </button>
                        <div className="flex items-center gap-4">
                          <button
                            type="button"
                            className="inline-flex items-center text-sm text-white dark:text-white gap-2 bg-primary-color hover:bg-secondary-color py-1.5 px-4 rounded-lg"
                            onClick={handleSaveCanvasAccountSettings}
                          >
                            {canvasDetailsIsEdit ? "Update" : "Add"}
                          </button>
                          <button
                            type="button"
                            onClick={onClose}
                            className="py-1.5 px-4 inline-flex items-center text-sm rounded-lg text-gray-700 border border-gray-300 gap-2 bg-white hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color"
                          >
                            <span>Cancel</span>
                          </button>
                        </div>
                      </div>
                    </form>

                    {canvasSchools.length > 0 && (
                      <div className="mt-6">
                        <h2 className="text-lg font-light text-gray-900 mb-2">
                          Schools
                        </h2>
                        <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg overflow-hidden">
                          <thead className="bg-primary-color">
                            <tr>
                              <th className="px-3 py-2 text-left text-sm font-light text-white tracking-wider">
                                School Id
                              </th>
                              <th className="px-3 py-2 text-left text-sm font-light text-white tracking-wider">
                                Name
                              </th>
                              <th className="px-3 py-2 text-left text-sm font-light text-white tracking-wider">
                                Group (Consortium)
                              </th>
                              <th className="px-3 py-2 text-left text-sm font-light text-white tracking-wider">
                                Scoring Type
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {canvasSchools.map((school, i) => (
                              <tr
                                key={school.schoolId}
                                className={
                                  i % 2 === 0 ? "bg-white" : "bg-gray-100"
                                }
                              >
                                <td className="px-3 py-2 text-sm">
                                  {school.schoolId}
                                </td>
                                <td className="px-3 py-2 text-sm">
                                  {school.name}
                                </td>
                                <td className="px-3 py-2 text-sm">
                                  <Select
                                    className="text-sm"
                                    value={
                                      groupOptions.find(
                                        (o) => o.value === school.groupNumber
                                      ) ?? groupOptions[0]
                                    }
                                    options={groupOptions}
                                    onChange={(option) =>
                                      handleSchoolCanvasGroupNumberChange(
                                        school.schoolId,
                                        option?.value ?? groupOptions[0].value
                                      )
                                    }
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    styles={{
                                      menuPortal: (base) => ({
                                        ...base,
                                        zIndex: 9999,
                                      }),
                                    }}
                                    theme={(theme) => ({
                                      ...theme,
                                      colors: {
                                        ...theme.colors,
                                        primary25: "#B3B3B3",
                                        primary: "#041E42",
                                      },
                                    })}
                                  />
                                </td>
                                <td className="px-3 py-2 text-sm">
                                  <Select
                                    className="text-sm"
                                    value={
                                      canvasScoringTypeOptionsForSchools.find(
                                        (o) =>
                                          o.value === school.canvasScoringTypeId
                                      ) ?? canvasScoringTypeOptionsForSchools[0]
                                    }
                                    options={canvasScoringTypeOptionsForSchools}
                                    onChange={(option) =>
                                      handleSchoolCanvasScoringTypeChange(
                                        school.schoolId,
                                        option?.value ?? null
                                      )
                                    }
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    styles={{
                                      menuPortal: (base) => ({
                                        ...base,
                                        zIndex: 9999,
                                      }),
                                    }}
                                    theme={(theme) => ({
                                      ...theme,
                                      colors: {
                                        ...theme.colors,
                                        primary25: "#B3B3B3",
                                        primary: "#041E42",
                                      },
                                    })}
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <div className="flex justify-end items-center gap-4">
                          <button
                            type="button"
                            className="mt-4 inline-flex items-center justify-end text-sm text-white dark:text-white gap-2 bg-primary-color hover:bg-secondary-color py-1.5 px-4 rounded-lg"
                            onClick={handleSaveCanvasAccountSchools}
                            disabled={canvasSchools.length === 0}
                          >
                            Update Schools
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
